import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  ChevronDown, ChevronRight, FileText, Upload, Search, Download, Send, Plus,
  Loader2, X, Link as LinkIcon, Copy, Clock, CheckCircle, Archive, ChevronLeft, ChevronLast, ChevronFirst,
  ClipboardList
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import Autocomplete from './ui/autocomplete';
import { Label } from './ui/label';

import { formatCPF, formatPhone, validateCPF, validatePhone, onlyNumbers } from '../lib/helpers';
import { useAuth } from '../context/AuthContext';
import { hasGranularPermission } from '../lib/permissions';

const API = process.env.REACT_APP_BACKEND_URL;
const PER_PAGE = 10;

const RH_CATEGORIAS = [
  { id: 'todos', nome: 'Todos os Documentos' },
  { id: 'admissao', nome: 'Admissão' },
  { id: 'demissao', nome: 'Demissão' },
  { id: 'atestados', nome: 'Atestados' },
  { id: 'advertencias', nome: 'Advertências' },
  { id: 'outros', nome: 'Outros' },
];

// Exame admissional — orientações e prazos (base: NR-7/PCMSO e CLT Art. 168)
const ADMISSAO_INFO = {
  prazo: 'O exame admissional deve ser realizado e o ASO emitido antes do colaborador iniciar as atividades. Nenhuma admissão deve ser efetivada sem o ASO admissional com resultado "Apto" ou "Apto com restrição".',
  orientacoes: [
    'Agendar o exame com antecedência para que o ASO fique pronto antes da data de início combinada.',
    'Verificar no PCMSO/PGR quais exames complementares são exigidos para a função e informar o colaborador (ex.: necessidade de jejum).',
    'Reunir os documentos de admissão (RG/CPF, comprovante de residência, escolaridade, foto 3x4, ASO admissional) e anexá-los aqui, nesta categoria.',
    'Se o resultado do ASO for "Apto com restrição" ou "Inapto", validar com o gestor da área antes de confirmar a admissão.',
  ],
};

const STATUS_CONFIG = {
  pendente: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendente' },
  visualizado: { color: 'bg-blue-100 text-blue-800', label: 'Visualizado' },
  arquivado: { color: 'bg-gray-100 text-gray-800', label: 'Arquivado' },
  aguardando_assinatura: { color: 'bg-purple-100 text-purple-800', label: 'Aguardando Assinatura' },
  assinado: { color: 'bg-green-100 text-green-800', label: 'Assinado' },
};

const DocumentosRHPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const categoriasFiltradas = useMemo(() =>
    RH_CATEGORIAS.filter(cat => hasGranularPermission(user, 'documentos_rh', cat.id)),
    [user]
  );

  const [employeesData, setEmployeesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriaAtiva, setCategoriaAtiva] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    return tabFromUrl && RH_CATEGORIAS.some(c => c.id === tabFromUrl) ? tabFromUrl : 'todos';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [documentoSign, setDocumentoSign] = useState(null);
  const [funcionarios, setFuncionarios] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);

  const [uploadForm, setUploadForm] = useState({
    funcionario_id: '',
    tipo_documento_id: '',
    categoria_rh: '',
    descricao_outros: '',
    motivo: '',
    file: null
  });

  const [signers, setSigners] = useState([{
    name: '',
    email: '',
    phone: '',
    cpf: ''
  }]);

  const [enviando, setEnviando] = useState(false);
  const [carregandoFuncionario, setCarregandoFuncionario] = useState(false);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [documentoLinks, setDocumentoLinks] = useState(null);
  const [showSuccessUrlsModal, setShowSuccessUrlsModal] = useState(false);
  const [successUrls, setSuccessUrls] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [selectedFuncionarioId, setSelectedFuncionarioId] = useState(null);
  const [totalArquivados, setTotalArquivados] = useState(0);

  const searchTimeoutRef = useRef(null);

  const totalPages = Math.ceil(totalEmployees / PER_PAGE);

  useEffect(() => {
    loadFuncionarios();
    loadTiposDocumento();
  }, []);

  // Sincronizar categoria quando a URL mudar
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && tabFromUrl !== categoriaAtiva && RH_CATEGORIAS.some(c => c.id === tabFromUrl)) {
      setCategoriaAtiva(tabFromUrl);
      setPage(1);
      setExpandedEmployees(new Set());
    }
  }, [location.search]);

  useEffect(() => {
    loadDocumentos();
  }, [categoriaAtiva, showArchived, page, searchTerm]);

  const loadTiposDocumento = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tipos-documentos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiposDocumento(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos de documento:', error);
    }
  };

  const loadFuncionarios = async () => {
    try {
      const response = await axios.get(`${API}/api/employees`);
      setFuncionarios(response.data.filter(emp => emp.situacao === 'ATIVO'));
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const loadDocumentos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { page, per_page: PER_PAGE, show_archived: showArchived };
      if (categoriaAtiva !== 'todos') {
        params.categoria_rh = categoriaAtiva;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const response = await axios.get(`${API}/api/documentos/rh`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeesData(response.data.employees || []);
      setTotalEmployees(response.data.total || 0);
      setTotalArquivados(response.data.total_arquivados || 0);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast.error('Erro ao carregar documentos');
    }
    setLoading(false);
  };

  const allDocs = employeesData.flatMap(emp => emp.docs || []);

  const stats = {
    total: allDocs.length,
    aguardando: allDocs.filter(d => d.status === 'aguardando_assinatura').length,
    assinados: allDocs.filter(d => d.status === 'assinado').length,
    pendentes: allDocs.filter(d => d.status === 'pendente' || d.status === 'visualizado').length,
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pendente;
    return <Badge className={`${config.color} text-xs font-medium`}>{config.label}</Badge>;
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
    }, 400);
  };

  const handleDownload = async (doc, signed = false) => {
    try {
      const token = localStorage.getItem('token');
      const params = signed ? { signed: true } : {};
      const response = await axios.get(`${API}/api/documentos/download/${doc.id}`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: doc.arquivo_tipo || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const baseName = doc.arquivo_nome || 'documento';
      const ext = baseName.includes('.') ? baseName.substring(baseName.lastIndexOf('.')) : '.pdf';
      const nameWithoutExt = baseName.includes('.') ? baseName.substring(0, baseName.lastIndexOf('.')) : baseName;
      a.href = url;
      a.download = signed ? `${nameWithoutExt}_ASSINADO${ext}` : baseName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar documento');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.funcionario_id || !uploadForm.tipo_documento_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (!uploadForm.categoria_rh) {
      toast.error('Selecione a categoria RH');
      return;
    }
    if (uploadForm.categoria_rh === 'outros' && !uploadForm.descricao_outros) {
      toast.error('Informe a descrição para a categoria "Outros"');
      return;
    }

    setEnviando(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('funcionario_id', uploadForm.funcionario_id);
      formData.append('tipo_documento_id', uploadForm.tipo_documento_id);
      formData.append('document_category', 'rh');
      formData.append('categoria_rh', uploadForm.categoria_rh);
      formData.append('descricao_outros', uploadForm.categoria_rh === 'outros' ? uploadForm.descricao_outros : '');
      formData.append('motivo', uploadForm.motivo);

      await axios.post(`${API}/api/documentos/upload/admin`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Documento enviado com sucesso');
      setShowUploadModal(false);
      resetUploadForm();
      loadDocumentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar documento');
    }
    setEnviando(false);
  };

  const resetUploadForm = () => {
    setUploadForm({
      funcionario_id: '',
      tipo_documento_id: '',
      categoria_rh: '',
      descricao_outros: '',
      motivo: '',
      file: null
    });
  };

  const handleEnviarAssinatura = async () => {
    if (!documentoSign) return;
    const validSigners = signers.filter(s => s.name && s.email);
    if (validSigners.length === 0) {
      toast.error('Adicione pelo menos um signatário com nome e email');
      return;
    }
    for (const s of validSigners) {
      const cpfDigits = onlyNumbers(s.cpf);
      if (s.cpf && cpfDigits.length === 11 && !validateCPF(cpfDigits)) {
        toast.error(`CPF inválido para ${s.name}`);
        return;
      }
      const phoneDigits = onlyNumbers(s.phone);
      if (phoneDigits.length >= 10 && !validatePhone(phoneDigits)) {
        toast.error(`Telefone inválido para ${s.name}`);
        return;
      }
    }
    setEnviando(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API}/api/documentos/${documentoSign.id}/enviar-assinatura`,
        { signers: validSigners },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const urls = resp.data.signing_urls || [];
      setSuccessUrls(urls);
      setShowSignModal(false);
      setDocumentoSign(null);
      loadDocumentos();
      if (urls.length > 0) {
        setShowSuccessUrlsModal(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar para assinatura');
    }
    setEnviando(false);
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const toggleAllForFuncionario = (funcionarioId) => {
    const emp = employeesData.find(e => e.funcionario_id === funcionarioId);
    if (!emp) return;
    const funcDocs = emp.docs || [];
    const allSelected = funcDocs.every(d => selectedDocs.has(d.id));
    setSelectedDocs(prev => {
      const next = new Set(prev);
      funcDocs.forEach(d => { if (allSelected) next.delete(d.id); else next.add(d.id); });
      return next;
    });
  };

  const toggleEmployeeExpanded = (funcionarioId) => {
    setExpandedEmployees(prev => {
      const next = new Set(prev);
      if (next.has(funcionarioId)) next.delete(funcionarioId); else next.add(funcionarioId);
      return next;
    });
  };

  const handleBatchSend = async () => {
    if (selectedDocs.size === 0) return;
    const validSigners = signers.filter(s => s.name && s.email);
    if (validSigners.length === 0) {
      toast.error('Adicione pelo menos um signatário com nome e email');
      return;
    }
    for (const s of validSigners) {
      const cpfDigits = onlyNumbers(s.cpf);
      if (s.cpf && cpfDigits.length === 11 && !validateCPF(cpfDigits)) {
        toast.error(`CPF inválido para ${s.name}`);
        return;
      }
      const phoneDigits = onlyNumbers(s.phone);
      if (phoneDigits.length >= 10 && !validatePhone(phoneDigits)) {
        toast.error(`Telefone inválido para ${s.name}`);
        return;
      }
    }
    setEnviando(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API}/api/documentos/enviar-assinatura-lote`,
        { document_ids: Array.from(selectedDocs), signers: validSigners, document_category: "rh" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const allUrls = resp.data.results?.flatMap(r => r.signing_urls) || [];
      setSuccessUrls(allUrls);
      setShowSignModal(false);
      setSelectedDocs(new Set());
      setSelectedFuncionarioId(null);
      setDocumentoSign(null);
      loadDocumentos();
      if (allUrls.length > 0) {
        setShowSuccessUrlsModal(true);
      }
      toast.success(`${resp.data.total_sent} documento(s) enviado(s) para assinatura`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar para assinatura');
    }
    setEnviando(false);
  };

  const openBatchSignModal = async (funcionarioId, funcionarioNome) => {
    const emp = employeesData.find(e => e.funcionario_id === funcionarioId);
    const eligibleDocIds = (emp?.docs || [])
      .filter(d => d.status !== 'assinado' && d.status !== 'arquivado')
      .map(d => d.id);
    setSelectedFuncionarioId(funcionarioId);
    setSelectedDocs(new Set(eligibleDocIds));
    setDocumentoSign({ funcionario_id: funcionarioId, funcionario_nome: funcionarioNome });
    setCarregandoFuncionario(true);
    setShowSignModal(true);
    setSigners([{ name: funcionarioNome, email: '', phone: '', cpf: '' }]);
    if (funcionarioId) {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API}/api/employees/${funcionarioId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const emp = resp.data;
        setSigners([{ name: emp.nome || funcionarioNome, email: emp.email || '', phone: emp.telefone || '', cpf: emp.cpf || '' }]);
      } catch { /* usa fallback */ }
    }
    setCarregandoFuncionario(false);
  };

  const openSignModal = async (doc) => {
    setSelectedDocs(new Set());
    setSelectedFuncionarioId(null);
    setDocumentoSign(doc);
    setCarregandoFuncionario(true);
    setShowSignModal(true);

    if (doc.signing_urls?.length > 0) {
      setSigners(doc.signing_urls.map(u => ({
        name: u.signer_name || '',
        email: u.signer_email || '',
        phone: '',
        cpf: ''
      })));
      setCarregandoFuncionario(false);
      return;
    }

    setSigners([{
      name: doc.funcionario_nome || '',
      email: '',
      phone: '',
      cpf: ''
    }]);

    if (doc.funcionario_id) {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API}/api/employees/${doc.funcionario_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const emp = resp.data;
        setSigners([{
          name: emp.nome || doc.funcionario_nome || '',
          email: emp.email || '',
          phone: emp.telefone || '',
          cpf: emp.cpf || ''
        }]);
      } catch (err) {
        console.error('Erro ao buscar dados do funcionário:', err);
      }
    }
    setCarregandoFuncionario(false);
  };

  const addSigner = () => {
    setSigners([...signers, { name: '', email: '', phone: '', cpf: '' }]);
  };

  const removeSigner = (index) => {
    if (signers.length <= 1) return;
    setSigners(signers.filter((_, i) => i !== index));
  };

  const updateSigner = (index, field, value) => {
    const updated = [...signers];
    let formatted = value;
    if (field === 'cpf') formatted = formatCPF(value);
    else if (field === 'phone') formatted = formatPhone(value);
    updated[index] = { ...updated[index], [field]: formatted };
    setSigners(updated);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copiado!');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Link copiado!');
    }
  };

  const openLinksModal = (doc) => {
    setDocumentoLinks(doc);
    setShowLinksModal(true);
  };

  const getEmployeeStats = (docs) => ({
    total: docs.length,
    pendentes: docs.filter(d => d.status === 'pendente' || d.status === 'visualizado').length,
    aguardando: docs.filter(d => d.status === 'aguardando_assinatura').length,
    assinados: docs.filter(d => d.status === 'assinado').length,
    arquivados: docs.filter(d => d.status === 'arquivado').length,
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documentos RH</h1>
          <p className="text-sm text-gray-500">Gerenciamento de documentos de Recursos Humanos</p>
        </div>
        <Button onClick={() => setShowUploadModal(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Novo Documento
        </Button>
      </div>

      {/* Compact Stats + Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <Tabs value={categoriaAtiva} onValueChange={(v) => { setCategoriaAtiva(v); setPage(1); setExpandedEmployees(new Set()); }}>
                <TabsList className="h-8">
                  {categoriasFiltradas.map(cat => (
                    <TabsTrigger key={cat.id} value={cat.id} className="text-xs px-3 py-1">
                      {cat.nome}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  placeholder="Buscar por funcionário ou documento..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <Button
                variant={showArchived ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs gap-1"
                onClick={() => { setShowArchived(!showArchived); setPage(1); }}
              >
                <Archive className="h-3.5 w-3.5" />
                Arquivados ({totalArquivados})
              </Button>
            </div>
          </div>

          {/* Compact stats row */}
          <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
            <span><strong className="text-gray-900">{totalEmployees}</strong> funcionários</span>
            <span><strong className="text-gray-900">{stats.total}</strong> documentos</span>
            <span className="text-purple-600"><strong>{stats.aguardando}</strong> aguardando</span>
            <span className="text-green-600"><strong>{stats.assinados}</strong> assinados</span>
            <span className="text-yellow-600"><strong>{stats.pendentes}</strong> pendentes</span>
          </div>
        </CardContent>
      </Card>

      {categoriaAtiva === 'admissao' && (
        <Alert className="border-blue-200 bg-blue-50/60" data-testid="documentos-rh-admissao-info">
          <ClipboardList className="h-4 w-4 text-blue-700" />
          <AlertTitle className="text-blue-900">Exame Admissional — orientações e prazos</AlertTitle>
          <AlertDescription className="text-slate-700 space-y-2">
            <p><strong>Prazo:</strong> {ADMISSAO_INFO.prazo}</p>
            <div>
              <strong>Orientações:</strong>
              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                {ADMISSAO_INFO.orientacoes.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Document list */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : employeesData.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Nenhum documento encontrado</p>
              <p className="text-sm text-gray-500 mt-2">
                {categoriaAtiva === 'todos'
                  ? 'Clique em "Novo Documento" para adicionar'
                  : `Nenhum documento em "${RH_CATEGORIAS.find(c => c.id === categoriaAtiva)?.nome}"`}
              </p>
            </div>
          ) : (
            <>
              {employeesData.map(group => {
                const groupDocs = group.docs || [];
                const allSelected = groupDocs.every(d => selectedDocs.has(d.id));
                const someSelected = groupDocs.some(d => selectedDocs.has(d.id));
                const isExpanded = expandedEmployees.has(group.funcionario_id);
                const empStats = getEmployeeStats(groupDocs);

                return (
                  <div key={group.funcionario_id} className="border-b last:border-b-0">
                    {/* Collapsible header */}
                    <div
                      className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => toggleEmployeeExpanded(group.funcionario_id)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Checkbox
                          checked={allSelected || (someSelected ? 'indeterminate' : false)}
                          onCheckedChange={() => toggleAllForFuncionario(group.funcionario_id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                        )}
                        <span className="font-semibold text-gray-800 text-sm truncate">{group.funcionario_nome}</span>
                        <span className="text-xs text-gray-500 shrink-0">({group.total_docs} doc(s))</span>
                      </div>

                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {!isExpanded && (
                          <div className="flex items-center gap-1.5 mr-2">
                            {empStats.aguardando > 0 && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0">
                                <Clock className="h-3 w-3 mr-0.5" />{empStats.aguardando}
                              </Badge>
                            )}
                            {empStats.assinados > 0 && (
                              <Badge className="bg-green-100 text-green-700 text-xs px-1.5 py-0">
                                <CheckCircle className="h-3 w-3 mr-0.5" />{empStats.assinados}
                              </Badge>
                            )}
                            {empStats.pendentes > 0 && (
                              <Badge className="bg-yellow-100 text-yellow-700 text-xs px-1.5 py-0">
                                {empStats.pendentes} pend.
                              </Badge>
                            )}
                          </div>
                        )}

                        {groupDocs.some(d => d.status !== 'assinado' && d.status !== 'arquivado') && (
                          <Button
                            size="sm"
                            className="h-7 text-xs gap-1"
                            onClick={() => openBatchSignModal(group.funcionario_id, group.funcionario_nome)}
                          >
                            <Send className="h-3 w-3" /> Enviar
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="overflow-x-auto border-t">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead className="w-8 h-8"></TableHead>
                              <TableHead className="text-xs h-8">Tipo</TableHead>
                              <TableHead className="text-xs h-8">Categoria</TableHead>
                              <TableHead className="text-xs h-8">Status</TableHead>
                              <TableHead className="text-xs h-8">Data</TableHead>
                              <TableHead className="text-xs h-8 text-right">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {groupDocs.map(doc => (
                              <TableRow key={doc.id} className={`${selectedDocs.has(doc.id) ? 'bg-purple-50' : ''} ${doc.status === 'arquivado' ? 'opacity-60' : ''}`}>
                                <TableCell className="py-1.5">
                                  <Checkbox
                                    checked={selectedDocs.has(doc.id)}
                                    onCheckedChange={() => toggleDocSelection(doc.id)}
                                    disabled={doc.status === 'assinado' || doc.status === 'arquivado'}
                                  />
                                </TableCell>
                                <TableCell className="py-1.5 text-sm">{doc.tipo_documento_nome}</TableCell>
                                <TableCell className="py-1.5">
                                  <div className="flex items-center gap-1">
                                    <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                      {RH_CATEGORIAS.find(c => c.id === doc.categoria_rh)?.nome || doc.categoria_rh}
                                    </Badge>
                                    {doc.categoria_rh === 'outros' && doc.descricao_outros && (
                                      <span className="text-xs text-gray-400 truncate max-w-[100px]">{doc.descricao_outros}</span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="py-1.5">{getStatusBadge(doc.status)}</TableCell>
                                <TableCell className="py-1.5 text-xs text-gray-600">
                                  {doc.criado_em ? new Date(doc.criado_em).toLocaleDateString('pt-BR') : '-'}
                                </TableCell>
                                <TableCell className="py-1.5 text-right">
                                  <div className="flex items-center justify-end gap-0.5">
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownload(doc)} title="Baixar Original">
                                      <Download className="h-3.5 w-3.5" />
                                    </Button>
                                    {doc.status === 'assinado' && (
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownload(doc, true)} title="Baixar Assinado">
                                        <Download className="h-3.5 w-3.5 text-green-600" />
                                      </Button>
                                    )}
                                    {doc.signing_urls?.length > 0 && (
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openLinksModal(doc)} title="Links de Assinatura">
                                        <LinkIcon className="h-3.5 w-3.5" />
                                      </Button>
                                    )}
                                    {doc.status !== 'assinado' && doc.status !== 'arquivado' && (
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openSignModal(doc)} title="Enviar para Assinatura">
                                        <Send className="h-3.5 w-3.5" />
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Server-side pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-sm text-gray-600">
                    Página {page} de {totalPages} ({totalEmployees} funcionário(s))
                  </span>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => setPage(1)}>
                      <ChevronFirst className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-gray-600 mx-2">Page {page} sur {totalPages}</span>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
                      <ChevronLast className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal Upload */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Novo Documento RH</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>Funcionário *</Label>
              <Autocomplete
                value={uploadForm.funcionario_id}
                onValueChange={(v) => setUploadForm({...uploadForm, funcionario_id: v})}
                options={funcionarios}
                getOptionValue={(emp) => emp.id}
                getOptionLabel={(emp) => `${emp.nome} - ${emp.cargo}`}
                placeholder="Selecione o funcionário"
                emptyMessage="Nenhum funcionário encontrado"
              />
            </div>

            <div className="space-y-2">
              <Label>Categoria RH *</Label>
              <Select value={uploadForm.categoria_rh} onValueChange={(v) => setUploadForm({...uploadForm, categoria_rh: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categoriasFiltradas.filter(c => c.id !== 'todos').map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {uploadForm.categoria_rh === 'outros' && (
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Input
                  value={uploadForm.descricao_outros}
                  onChange={(e) => setUploadForm({...uploadForm, descricao_outros: e.target.value})}
                  placeholder="Descreva o tipo de documento"
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Tipo de Documento *</Label>
              <Select value={uploadForm.tipo_documento_id} onValueChange={(v) => setUploadForm({...uploadForm, tipo_documento_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposDocumento.map(tipo => (
                    <SelectItem key={tipo.id} value={tipo.id}>{tipo.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Input
                value={uploadForm.motivo}
                onChange={(e) => setUploadForm({...uploadForm, motivo: e.target.value})}
                placeholder="Motivo do documento"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Arquivo *</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                required
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowUploadModal(false); resetUploadForm(); }}>
                Cancelar
              </Button>
              <Button type="submit" disabled={enviando}>
                {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Enviar Documento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Enviar para Assinatura */}
      <Dialog open={showSignModal} onOpenChange={(open) => { if (!open) { setSelectedDocs(new Set()); setSelectedFuncionarioId(null); } setShowSignModal(open); }}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enviar para Assinatura</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedDocs.size > 0 ? (
              <p className="text-sm text-gray-600">
                <strong>{selectedDocs.size} documento(s)</strong> selecionado(s) de <strong>{documentoSign?.funcionario_nome}</strong>
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Documento: <strong>{documentoSign?.tipo_documento_nome}</strong> - {documentoSign?.funcionario_nome}
              </p>
            )}

            {carregandoFuncionario && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                <span className="ml-2 text-sm text-gray-500">Carregando dados do funcionário...</span>
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Signatários</Label>
                <Button type="button" variant="outline" size="sm" onClick={addSigner}>
                  <Plus className="h-4 w-4 mr-1" /> Adicionar
                </Button>
              </div>

              {signers.map((signer, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3 relative">
                  {signers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSigner(idx)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  <p className="text-xs text-gray-500 font-medium">Signatário {idx + 1}</p>
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input value={signer.name} onChange={(e) => updateSigner(idx, 'name', e.target.value)} placeholder="Nome completo" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" value={signer.email} onChange={(e) => updateSigner(idx, 'email', e.target.value)} placeholder="email@exemplo.com" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Telefone</Label>
                      <Input value={signer.phone} onChange={(e) => updateSigner(idx, 'phone', e.target.value)} placeholder="(11) 99999-9999" />
                    </div>
                    <div className="space-y-2">
                      <Label>CPF</Label>
                      <Input value={signer.cpf} onChange={(e) => updateSigner(idx, 'cpf', e.target.value)} placeholder="000.000.000-00" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setShowSignModal(false); setSelectedDocs(new Set()); setSelectedFuncionarioId(null); }}>Cancelar</Button>
              <Button onClick={selectedDocs.size > 0 ? handleBatchSend : handleEnviarAssinatura} disabled={enviando || carregandoFuncionario}>
                {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {selectedDocs.size > 0 ? `Enviar ${selectedDocs.size} Documento(s)` : 'Enviar para Assinatura'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Links de Assinatura */}
      <Dialog open={showLinksModal} onOpenChange={setShowLinksModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Links de Assinatura</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Documento: <strong>{documentoLinks?.tipo_documento_nome}</strong> - {documentoLinks?.funcionario_nome}
            </p>
            <div className="space-y-3">
              {(documentoLinks?.signing_urls || []).map((item, idx) => (
                <div key={idx} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{item.signer_name}</p>
                      <p className="text-xs text-gray-500">{item.signer_email}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(item.signing_url)}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copiar
                    </Button>
                  </div>
                  <div className="bg-gray-50 rounded p-2 text-xs text-gray-500 break-all">
                    {item.signing_url}
                  </div>
                </div>
              ))}
              {(!documentoLinks?.signing_urls || documentoLinks.signing_urls.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-4">Nenhum link de assinatura disponível.</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinksModal(false)}>Fechar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Sucesso - Links de Assinatura */}
      <Dialog open={showSuccessUrlsModal} onOpenChange={setShowSuccessUrlsModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Links de Assinatura Criados</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Copie os links abaixo para enviar aos signatários:
            </p>
            <div className="space-y-3">
              {successUrls.map((item, idx) => (
                <div key={idx} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{item.signer_name}</p>
                      <p className="text-xs text-gray-500">{item.signer_email}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(item.signing_url)}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copiar Link
                    </Button>
                  </div>
                  <div className="bg-gray-50 rounded p-2 text-xs text-gray-500 break-all font-mono">
                    {item.signing_url}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSuccessUrlsModal(false)}>Fechar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentosRHPage;
